import { useCallback, useEffect, useMemo, useState } from 'react'
import { env } from '@/lib/env'
import { getTokens, onTokensChanged } from '@/lib/auth-tokens'

/**
 * AF-402 — inactivity-driven session timer.
 *
 * Tracks the operator's last input event (mousemove, keydown, click,
 * scroll, touchstart) and exposes the time remaining before an
 * inactivity-driven sign-out. Multi-tab coordination via a shared
 * localStorage key + the storage event so activity in one tab
 * resets the countdown in every tab of the same browser.
 *
 * The timer is purely a UX guardrail — the server-side JWT TTL still
 * governs the hard cut. We refresh activity on every authenticated
 * API call too (via {@link onTokensChanged}); this is what stops a
 * user mid-keystroke from being logged out while typing into a long
 * narrative form.
 *
 * Three thresholds, all configurable:
 *
 *   - VITE_SESSION_TIMEOUT_MINUTES (default 15) — total idle window
 *   - VITE_SESSION_COUNTDOWN_MINUTES (default 5) — show topbar badge
 *   - VITE_SESSION_WARNING_MINUTES (default 2) — open warning modal
 *
 * The hook is a pure observer; the modal/badge components do their
 * own gating off `phase`.
 */

const STORAGE_KEY = 'antifraud.session.last_activity.v1'
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const
// Throttle activity writes — pumping localStorage on every mousemove
// would generate hundreds of storage events per second across tabs.
const ACTIVITY_WRITE_THROTTLE_MS = 5_000
// Tick interval for the countdown rerender. 1s is fine for a clock
// that displays mm:ss; the underlying timestamp is exact regardless.
const TICK_MS = 1_000

export type SessionPhase = 'fresh' | 'countdown' | 'warning' | 'expired'

export interface SessionTimerState {
  /** Milliseconds until inactivity-driven sign-out (0 = expired). */
  remainingMs: number
  phase: SessionPhase
  /** Last activity wall-clock timestamp (ms since epoch). */
  lastActivity: number
  /**
   * Reset the inactivity timer (call from "Stay signed in"). Writes
   * the new timestamp to localStorage so other tabs see it via the
   * storage event.
   */
  bump: () => void
  /** Active session windows in ms — convenience for callers. */
  windows: {
    timeoutMs: number
    countdownMs: number
    warningMs: number
  }
}

export function useSessionTimer(): SessionTimerState {
  const windows = useMemo(
    () => ({
      timeoutMs: env.VITE_SESSION_TIMEOUT_MINUTES * 60_000,
      countdownMs: env.VITE_SESSION_COUNTDOWN_MINUTES * 60_000,
      warningMs: env.VITE_SESSION_WARNING_MINUTES * 60_000,
    }),
    [],
  )

  const [lastActivity, setLastActivity] = useState<number>(() => readActivity())
  const [, tick] = useState(0)

  // ---- Activity capture --------------------------------------------
  // Throttled writer — only persists once per ACTIVITY_WRITE_THROTTLE_MS.
  // The closure is stable for the listener's lifetime so we don't
  // re-bind every render.
  useEffect(() => {
    if (!hasAuth()) return
    let lastWrite = 0
    const onActivity = () => {
      const now = Date.now()
      if (now - lastWrite < ACTIVITY_WRITE_THROTTLE_MS) return
      lastWrite = now
      writeActivity(now)
      setLastActivity(now)
    }
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true })
    }
    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity)
      }
    }
  }, [])

  // ---- Cross-tab sync -----------------------------------------------
  // Storage events fire when *another* tab writes to localStorage.
  // We mirror the value into local state so this tab's countdown
  // resets too.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== window.localStorage) return
      if (e.key !== STORAGE_KEY) return
      const next = parseTs(e.newValue)
      if (next != null) setLastActivity(next)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // ---- API-driven refresh ------------------------------------------
  // Every successful authenticated API call refreshes tokens; treat
  // that as activity too. Without this, an analyst who's typing a
  // long SAR narrative into a controlled textarea (which fires
  // keydown so we already cover it, but defense-in-depth here) or
  // who's idle while a long-running mutation is in flight could
  // expire mid-call.
  useEffect(() => {
    return onTokensChanged(() => {
      const now = Date.now()
      writeActivity(now)
      setLastActivity(now)
    })
  }, [])

  // ---- Countdown ticker --------------------------------------------
  // Rerender once per second so consumers see remainingMs update.
  // The "current time" is held in state and refreshed by the interval
  // so the render path stays pure (no Date.now() during render).
  const [now, setNow] = useState<number>(() => Date.now())
  useEffect(() => {
    if (!hasAuth()) return
    const id = window.setInterval(() => {
      tick((n) => n + 1)
      setNow(Date.now())
    }, TICK_MS)
    return () => window.clearInterval(id)
  }, [])

  const elapsed = Math.max(0, now - lastActivity)
  const remainingMs = Math.max(0, windows.timeoutMs - elapsed)
  const phase = computePhase(remainingMs, windows)

  const bump = useCallback(() => {
    const t = Date.now()
    writeActivity(t)
    setLastActivity(t)
  }, [])

  return { remainingMs, phase, lastActivity, bump, windows }
}

// ---- helpers --------------------------------------------------------

function hasAuth(): boolean {
  return getTokens() != null
}

function readActivity(): number {
  if (typeof window === 'undefined') return Date.now()
  const raw = window.localStorage.getItem(STORAGE_KEY)
  const parsed = parseTs(raw)
  // First load: treat *now* as the start of the idle window. Without
  // this, a session that was last active 20 minutes ago in a closed
  // tab would auto-expire the moment we open a new tab — annoying
  // because the user is clearly back at the keyboard.
  if (parsed == null) {
    const t = Date.now()
    writeActivity(t)
    return t
  }
  return parsed
}

function writeActivity(ts: number): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, String(ts))
  } catch {
    // Quota / private-mode: in-memory state still works for this tab.
  }
}

function parseTs(raw: string | null): number | null {
  if (raw == null) return null
  const n = Number.parseInt(raw, 10)
  return Number.isFinite(n) ? n : null
}

function computePhase(
  remainingMs: number,
  windows: { countdownMs: number; warningMs: number },
): SessionPhase {
  if (remainingMs <= 0) return 'expired'
  if (remainingMs <= windows.warningMs) return 'warning'
  if (remainingMs <= windows.countdownMs) return 'countdown'
  return 'fresh'
}

/** Format a duration as mm:ss for display. */
export function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

// Test-only — vitest can call this between tests so the localStorage
// mirror doesn't bleed across cases.
export function __resetSessionTimerForTests(): void {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(STORAGE_KEY)
  }
}
